import { useState, useEffect, useCallback, useRef } from 'react';

const STORAGE_KEY = 'engraveca_recently_viewed';
const MAX_ITEMS = 8;

// Get initial state from localStorage (runs once)
const getInitialState = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    }
  } catch (error) {
    console.error('Error loading recently viewed:', error);
  }
  return [];
};

/**
 * Hook to manage recently viewed products
 * Stores in localStorage, max 8 items, excludes current product
 */
export const useRecentlyViewed = () => {
  const [recentlyViewed, setRecentlyViewed] = useState(getInitialState);
  const isInitialized = useRef(false);

  // Sync to localStorage when state changes (but not on initial load)
  useEffect(() => {
    if (isInitialized.current) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(recentlyViewed));
      } catch (error) {
        console.error('Error saving recently viewed:', error);
      }
    } else {
      isInitialized.current = true;
    }
  }, [recentlyViewed]);

  // Add a product to recently viewed
  const addToRecentlyViewed = useCallback((product) => {
    if (!product || !product.id) return;

    setRecentlyViewed(prev => {
      // Remove if already exists (will re-add at front)
      const filtered = prev.filter(p => p.id !== product.id);
      
      // Create minimal product object
      const newItem = {
        id: product.id,
        name: product.name,
        image: product.image,
        price: product.basePrice || product.priceRange?.min || 0,
        priceMax: product.priceRange?.max || null,
        section: product.section || 'small-format',
        category: product.category || '',
        viewedAt: Date.now()
      };

      // Add to front, limit to MAX_ITEMS
      return [newItem, ...filtered].slice(0, MAX_ITEMS);
    });
  }, []);

  // Get recently viewed excluding a specific product ID
  const getRecentlyViewed = useCallback((excludeId = null) => {
    if (!excludeId) return recentlyViewed;
    return recentlyViewed.filter(p => p.id !== excludeId);
  }, [recentlyViewed]);

  // Clear all recently viewed
  const clearRecentlyViewed = useCallback(() => {
    setRecentlyViewed([]);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing recently viewed:', error);
    }
  }, []);

  return {
    recentlyViewed,
    addToRecentlyViewed,
    getRecentlyViewed,
    clearRecentlyViewed
  };
};

export default useRecentlyViewed;
